/**
 * Badge Icon Component
 * Displays badge icon with fallback and cache-busting support
 */

import { useState } from 'react';
import { cn } from '../../lib/utils';

interface BadgeIconProps {
  src: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * True when the badge has no icon BY DESIGN (#1975), as opposed to one whose
   * icon has not been uploaded yet. Both arrive here as `src=''`, so without
   * this the component cannot tell them apart and paints the same "something
   * is missing" tile for each.
   */
  imageless?: boolean;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

export function BadgeIcon({
  src,
  alt,
  size = 'md',
  className = '',
  imageless = false,
}: BadgeIconProps) {
  // A badge with no icon yet carries `icon_url: ''`. Skip straight to the
  // fallback rather than rendering an <img> and waiting to be told it failed.
  //
  // The comment that used to sit here said a blank src "fires neither `load`
  // nor `error`". That is not true of current browsers, and it was the stated
  // reason for this line. Measured: `el.src = ''` and `setAttribute('src','')`
  // both fire `error` in Chrome 146 and Firefox 153, and React renders
  // `src=""` (not an omitted attribute) for `src={''}`, so the one case that
  // really fires nothing — no attribute at all — is unreachable from here.
  // Nor does an empty src re-request the page: that folklore is long obsolete.
  //
  // The line stays because deciding up front is still better than deciding on
  // an event: no wasted request, and no frame where the skeleton shows for an
  // image we already know is absent.
  //
  // `imageless` short-circuits the same way. The server forces `icon_url: ''`
  // for such a badge, so `src` should already be empty — but deriving this
  // from the flag rather than trusting the URL means a stale cached definition
  // cannot make an intentionally iconless badge fetch something.
  const hasSrc = !imageless && Boolean(src);

  // State is stored AS the source it describes, and the flags are derived from
  // it, rather than being reset in an effect when `src` changes. An effect runs
  // after commit, so for an image served from cache — where `load` fires first —
  // the reset would land after the image had already reported success and pin
  // the skeleton on forever, with no second `load` event coming to clear it.
  // Keying the state to the source makes a change self-correcting during render.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const hasError = hasSrc && failedSrc === src;
  const isLoading = hasSrc && loadedSrc !== src && !hasError;
  const handleLoad = () => setLoadedSrc(src);
  const handleError = () => setFailedSrc(src);

  // Fall back whenever there is no usable source, not just on a load failure —
  // but NOT for an imageless badge, which gets its own treatment below. The
  // two used to share this branch, which is exactly the conflation #1975
  // exists to remove: "nobody has uploaded this yet" and "this will never have
  // one" are opposite states and were drawing the same grey tile.
  const showFallback = !imageless && (hasError || !hasSrc);

  return (
    <div className={cn('relative', sizeClasses[size], className)}>
      {/* Loading skeleton. It sits BEHIND the image rather than the image being
          hidden until a `load` event arrives.

          Whether that event is observed is a race the component cannot win:
          an image already decoded when React attaches its handler fires no
          `load` at all, and the browser is free to complete it at any point
          around mount. Two shipped bugs came from trying to arbitrate that —
          first a skeleton pinned on forever by an effect, then a decoded icon
          held at opacity-0 on a cold load.

          Layering removes the question. `isLoading` now only decides whether a
          placeholder is painted underneath; if it is ever wrong, the worst case
          is a skeleton nobody can see behind an opaque icon, never a good image
          made invisible. */}
      {hasSrc && isLoading && !hasError && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-lg" />
      )}

      {/* Main image */}
      {hasSrc && !hasError && (
        <img
          src={src}
          alt={alt}
          className="relative w-full h-full object-cover rounded-lg"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* No icon by design. Deliberately unlike the tile below: dashed and
          unfilled rather than a solid grey block, so an admin scanning a
          catalogue can see at a glance which badges are still waiting for
          artwork and which never will be.

          `aria-hidden` because the badge's name is visible text immediately
          beside this at every call site, so an accessible name here would
          announce the rendering ("no icon") rather than the badge. W3C WAI's
          decorative-images guidance and WCAG H67 both say a competing label
          next to visible text hurts rather than helps. The fallback tile below
          carries no accessible name either, so the two stay consistent. */}
      {imageless && (
        <div
          aria-hidden="true"
          className="w-full h-full rounded-lg border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center"
        >
          <svg
            className="w-1/2 h-1/2 text-gray-400 dark:text-gray-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2l3.354 1.934a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" />
          </svg>
        </div>
      )}

      {/* Placeholder for a badge whose icon has not been uploaded YET, or one
          that failed to load. Not for an imageless badge — that is the block
          above, and telling the two apart is the whole point of #1975.

          This used to be preceded by an <img> pointing at
          api.dicebear.com/7.x/shapes/svg?seed=<badgeId>, so every badge without
          a usable icon announced its id to a third party on every render, on
          every screen this component appears. #1958 batch 2a stopped that URL
          being written into the database; this stops it being requested at
          all. The local drawing below was already here, behind the remote one. */}
      {showFallback && (
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
          <svg
            className="w-1/2 h-1/2 text-gray-400 dark:text-gray-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
